import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconButton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import clsx from 'clsx';

import { formatTimeValueByConfig } from '../../../../utils/datetime/date-time-utils';
import AppMenu, { PositionOption, getPosition } from '../../app-menu/AppMenu';
import AppStandardTextField from '../../textfields/app-standart-text-field/AppStandardTextField';
import AppTextField from '../../textfields/app-text-field/AppTextField';
import { TimeWheelPicker } from '../app-time-wheel-picker/AppTimeWheelPicker';
import { useStyles } from './AppTimeSetterField.styles';

export type TimePickerStep = 1 | 5 | 10 | 15;

export interface TimePickerOptions {
  showHours?: boolean;
  showMinutes?: boolean;
  showSeconds?: boolean;
  maxHours?: number;
  maxMinutes?: number;
  maxSeconds?: number;
  minuteStep?: TimePickerStep;
  secondStep?: TimePickerStep;
}

interface AppTimeSetterFieldProps {
  value: number | null;
  onChange: (value: number) => void;
  label?: React.ReactNode;
  placeholder?: string | number;
  classes?: Record<string, string>;
  viewMode?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  pickerOptions?: TimePickerOptions;
  textFieldVariant?: 'standard' | 'default';
}

const AppTimeSetterField: React.FC<AppTimeSetterFieldProps> = (props) => {
  const {
    value,
    onChange,
    label,
    classes,
    viewMode,
    placeholder,
    fullWidth,
    disabled = false,
    error,
    helperText,
    pickerOptions = {},
    textFieldVariant = 'standard',
  } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { classes: internalClasses } = useStyles({ fullWidth });

  const maxHours = pickerOptions.maxHours ?? 23;
  const maxMinutes = pickerOptions.maxMinutes ?? 59;
  const maxSeconds = pickerOptions.maxSeconds ?? 59;
  const minuteStep = pickerOptions.minuteStep ?? 1;
  const secondStep = pickerOptions.secondStep ?? 1;
  const safeValue = value ?? 0;

  const snapToStep = (val: number, step: number, max: number) =>
    Math.min(max, Math.round(val / step) * step);

  const [internalValue, setInternalValue] = useState(() => {
    const hour = Math.min(Math.floor((safeValue / 3600) % 24) || 0, maxHours);
    const minute = snapToStep(
      Math.floor((safeValue / 60) % 60) || 0,
      minuteStep,
      maxMinutes,
    );
    const second = snapToStep(
      Math.floor(safeValue % 60) || 0,
      secondStep,
      maxSeconds,
    );
    return { hour, minute, second };
  });

  useEffect(() => {
    if (anchorEl) {
      const hour = Math.min(Math.floor((safeValue / 3600) % 24) || 0, maxHours);
      const minute = snapToStep(
        Math.floor((safeValue / 60) % 60) || 0,
        minuteStep,
        maxMinutes,
      );
      const second = snapToStep(
        Math.floor(safeValue % 60) || 0,
        secondStep,
        maxSeconds,
      );
      setInternalValue({ hour, minute, second });
    }
  }, [
    anchorEl,
    safeValue,
    maxHours,
    maxMinutes,
    maxSeconds,
    minuteStep,
    secondStep,
  ]);

  const setTime = () => {
    const v =
      internalValue.second +
      internalValue.minute * 60 +
      internalValue.hour * 3600;
    onChange(v);
    setAnchorEl(null);
  };

  const displayConfig = useMemo(
    () => ({
      showHours: pickerOptions.showHours ?? false,
      showMinutes: pickerOptions.showMinutes ?? true,
      showSeconds: pickerOptions.showSeconds ?? true,
    }),
    [
      pickerOptions.showHours,
      pickerOptions.showMinutes,
      pickerOptions.showSeconds,
    ],
  );

  const displayValue = useMemo(
    () =>
      value === null ? '' : formatTimeValueByConfig(value, t, displayConfig),
    [value, t, displayConfig],
  );

  const viewPlaceholder: string = useMemo<string>(
    () =>
      Number.isInteger(placeholder)
        ? formatTimeValueByConfig(placeholder as number, t, displayConfig)
        : (placeholder as string),
    [placeholder, t, displayConfig],
  );

  return (
    <AppMenu
      anchorEl={anchorEl}
      onClose={() => setAnchorEl(null)}
      anchorOrigin={getPosition(PositionOption.bottom, PositionOption.right)}
      transformOrigin={getPosition(PositionOption.top, PositionOption.right)}
      className={clsx(internalClasses.menuContainer, classes?.menuContainer)}
      targetElement={
        textFieldVariant === 'default' ? (
          <AppTextField
            label={label}
            value={displayValue || ''}
            fullWidth
            className={classes?.textField}
            disabled={disabled}
            placeholder={viewPlaceholder}
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (viewMode || disabled) return;
              setAnchorEl(e.currentTarget);
            }}
          />
        ) : (
          <AppStandardTextField
            label={label}
            value={displayValue || null}
            fullWidth
            className={classes?.textField}
            disabled={disabled}
            placeholder={viewPlaceholder}
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (viewMode || disabled) return;
              setAnchorEl(e.currentTarget);
            }}
          />
        )
      }>
      <div className={internalClasses.timeSetterHeader}>
        <IconButton
          aria-label={t('common.cancel')}
          size="small"
          onClick={() => setAnchorEl(null)}
          sx={{ color: 'text.secondary' }}>
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>

        <IconButton
          aria-label={t('common.setValue')}
          size="small"
          onClick={setTime}
          sx={{ color: 'primary.main' }}>
          <CheckIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </div>

      <div className={internalClasses.timeSetterWrapper}>
        <TimeWheelPicker
          onChange={(v) => {
            setInternalValue((prev) => ({ ...prev, ...v }));
          }}
          value={internalValue}
          options={{
            containerHeight: isMobile ? 252 : 210,
            itemHeight: isMobile ? 40 : 32,
            hoursFormat: 24,
            showHours: pickerOptions.showHours ?? false,
            showMinutes: pickerOptions.showMinutes ?? true,
            showSeconds: pickerOptions.showSeconds ?? true,
            maxHours: pickerOptions.maxHours ?? 24,
            maxMinutes: pickerOptions.maxMinutes ?? 59,
            maxSeconds: pickerOptions.maxSeconds ?? 59,
            minuteStep,
            secondStep,
          }}
        />
      </div>
    </AppMenu>
  );
};

export default AppTimeSetterField;
